import { BoatFilterSidebar, type BoatFilters } from '@/components/boat-filter-sidebar';
import { type BoatOfferCardProps } from '@/components/boat-offer-card';
import { BoatResultsGrid } from '@/components/boat-results-grid';
import { BoatResultsHeader, type SortOption } from '@/components/boat-results-header';
import { ListingFilterSidebar } from '@/components/listing-filter-sidebar';
import { ListingResultsGrid } from '@/components/listing-results-grid';
import { SearchAutocomplete } from '@/components/search-autocomplete';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import FrontendLayout from '@/layouts/frontend-layout';
import { apiClient } from '@/lib/apiClient';
import { buildClassifiedPath } from '@/lib/classified-url';
import { getPublicPriceLabel } from '@/lib/currency';
import { getMediaUrl } from '@/lib/media';
import { login, search } from '@/routes';
import { show as showListing } from '@/routes/listings';
import {
    type CategoryBreadcrumbItem,
    type CategoryFaqItem,
    type CategorySeoData,
    type Classified,
    type FilterOption,
    type FilterOptions,
    type Listing,
    type ListingCardProps,
    type ListingFilters,
    type MediaItem,
    type PaginatedData,
    type SearchFilters,
    type SharedData,
} from '@/types';
import { Head, router, usePage } from '@inertiajs/react';
import DOMPurify from 'isomorphic-dompurify';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface Banner {
    id: number;
    title: string;
    link_url: string | null;
    section: string;
    is_active: boolean;
    description: string | null;
    image_alt: string | null;
    desktop_image: MediaItem | null;
    mobile_image: MediaItem | null;
    views_count: number;
    clicks_count: number;
}

interface Props {
    results: PaginatedData<Classified | Listing>;
    filterOptions: FilterOptions;
    filters: SearchFilters;
    type?: 'embarcacoes' | 'empresas';
    classifiedBanners?: Banner[];
    categorySeo?: CategorySeoData | null;
}

type ResultsType = 'embarcacoes' | 'empresas';
type InertiaPayload = Record<string, string | number | boolean | null | undefined | string[] | number[]>;

interface ResultsSearchParams extends Omit<SearchFilters, 'type'> {
    type?: ResultsType;
}

const DEFAULT_YEAR_MIN = 1990;
const DEFAULT_PRICE_MAX = 1000000;
const DEFAULT_SAILING_MAX = 10000;
const FALLBACK_RESULT_IMAGE = '/boat-1.jpg';
const ALLOWED_CONTENT_TAGS = ['p', 'br', 'ul', 'ol', 'li', 'strong', 'b', 'em', 'i', 'u', 'blockquote', 'a', 'h2', 'h3', 'h4'];
const ALLOWED_CONTENT_ATTRIBUTES = ['href', 'target', 'rel'];

const normalizeCategory = (category: SearchFilters['category']) => {
    if (!category) {
        return [];
    }

    if (Array.isArray(category)) {
        return category.map(String);
    }

    return [String(category)];
};

const normalizeStateSlugs = (state: SearchFilters['state'] | number | string | string[] | undefined): string[] => {
    if (Array.isArray(state)) {
        return state.map(String).filter((s) => s.length > 0);
    }

    if (state === undefined || state === null || state === '') {
        return [];
    }

    return [String(state)];
};

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(value, max));

const formatLocation = (cityName?: string | null, stateName?: string | null) => {
    if (cityName && stateName) {
        return `${cityName}, ${stateName}`;
    }

    return cityName || stateName || '';
};

const normalizeText = (value?: string | null) => {
    if (!value) {
        return '';
    }

    return value.trim();
};

const stripHtml = (value: string) => {
    return value
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, ' ')
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
};

const sanitizeHtmlContent = (value: string) => {
    return DOMPurify.sanitize(value, {
        ALLOWED_TAGS: ALLOWED_CONTENT_TAGS,
        ALLOWED_ATTR: ALLOWED_CONTENT_ATTRIBUTES,
        FORBID_TAGS: ['script', 'style'],
        ALLOW_DATA_ATTR: false,
    }).trim();
};

const truncateText = (value: string, maxLength: number) => {
    if (value.length <= maxLength) {
        return value;
    }

    return `${value.slice(0, Math.max(maxLength - 1, 1)).trimEnd()}…`;
};

const toAbsoluteUrl = (appUrl: string, path: string) => {
    try {
        if (/^https?:\/\//i.test(path)) {
            return path;
        }

        const base = appUrl?.trim() ? (appUrl.endsWith('/') ? appUrl : `${appUrl}/`) : window.location.origin;

        return new URL(path, base).toString();
    } catch {
        return path;
    }
};

const normalizeStringArray = (value: unknown): string[] => {
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value.map((item) => (typeof item === 'string' ? item.trim() : '')).filter((item) => item.length > 0);
    }

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return [];
        }

        try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
                return parsed.map((item) => (typeof item === 'string' ? item.trim() : '')).filter((item) => item.length > 0);
            }
        } catch {
            return trimmed
                .split(/\r\n|\r|\n/)
                .map((item) => item.trim())
                .filter((item) => item.length > 0);
        }
    }

    return [];
};

const normalizeFaqItems = (value: unknown): CategoryFaqItem[] => {
    if (!value) {
        return [];
    }

    const rawItems = Array.isArray(value)
        ? value
        : typeof value === 'string'
          ? (() => {
                try {
                    const parsed = JSON.parse(value);
                    return Array.isArray(parsed) ? parsed : [];
                } catch {
                    return [];
                }
            })()
          : [];

    return rawItems
        .map((item) => {
            if (!item || typeof item !== 'object') {
                return null;
            }

            const entry = item as Record<string, unknown>;
            const questionSource = entry.question ?? entry.pergunta ?? entry.name;
            const answerSource = entry.answer ?? entry.resposta ?? entry.text;
            const question = typeof questionSource === 'string' ? questionSource.trim() : '';
            const answer = typeof answerSource === 'string' ? answerSource.trim() : '';

            if (!question || !answer) {
                return null;
            }

            return {
                question,
                answer,
            };
        })
        .filter((item): item is CategoryFaqItem => Boolean(item));
};

const normalizeBreadcrumbs = (value?: CategoryBreadcrumbItem[] | null): CategoryBreadcrumbItem[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .filter((item): item is CategoryBreadcrumbItem => Boolean(item?.name && item?.path))
        .map((item) => ({
            name: item.name.trim(),
            path: item.path,
        }))
        .filter((item) => item.name.length > 0);
};

const buildInitialBoatFilters = (initialFilters: SearchFilters, filterOptions: FilterOptions): BoatFilters => {
    const yearMin = filterOptions?.yearRange?.min ?? DEFAULT_YEAR_MIN;
    const yearMax = filterOptions?.yearRange?.max ?? new Date().getFullYear();
    const priceMin = filterOptions?.priceRange?.min ?? 0;
    const priceMax = filterOptions?.priceRange?.max ?? DEFAULT_PRICE_MAX;
    const initialYearMin = clamp(initialFilters.year_min ?? yearMin, yearMin, yearMax);
    const initialYearMax = clamp(initialFilters.year_max ?? yearMax, yearMin, yearMax);
    const initialPriceMin = clamp(initialFilters.price_min ?? priceMin, priceMin, priceMax);
    const initialPriceMax = clamp(initialFilters.price_max ?? priceMax, priceMin, priceMax);
    const initialHoursMin = clamp(initialFilters.sailing_time_min ?? 0, 0, DEFAULT_SAILING_MAX);
    const initialHoursMax = clamp(initialFilters.sailing_time_max ?? DEFAULT_SAILING_MAX, 0, DEFAULT_SAILING_MAX);

    return {
        category: normalizeCategory(initialFilters.category),
        condition: initialFilters.condition ?? 'all',
        location: normalizeStateSlugs(initialFilters.state),
        yearRange: [Math.min(initialYearMin, initialYearMax), Math.max(initialYearMin, initialYearMax)],
        priceRange: [Math.min(initialPriceMin, initialPriceMax), Math.max(initialPriceMin, initialPriceMax)],
        hours: [Math.min(initialHoursMin, initialHoursMax), Math.max(initialHoursMin, initialHoursMax)],
    };
};

const buildInitialListingFilters = (initialFilters: SearchFilters): ListingFilters => ({
    category: normalizeCategory(initialFilters.category),
    location: normalizeStateSlugs(initialFilters.state),
});

const hasHttpStatus = (error: unknown, status: number) => {
    if (typeof error !== 'object' || error === null || !('response' in error)) {
        return false;
    }

    const response = (error as { response?: { status?: number } }).response;
    return response?.status === status;
};

const toBoatCards = (classifieds: Classified[], favoriteStates: Record<number, boolean>): BoatOfferCardProps[] => {
    return classifieds.map((classified) => ({
        id: classified.id,
        title: classified.title,
        location: formatLocation(classified.city?.name, classified.state?.name),
        price: getPublicPriceLabel({
            price: classified.price,
            priceOnRequest: classified.price_on_request,
        }),
        year: classified.year?.toString() || '--',
        engine_quantity: classified.engine_quantity ?? 0,
        engine_model: classified.engine_model,
        condition: classified.condition === 'new' ? 'Novo' : 'Usado',
        imageUrl: getMediaUrl(classified.main_image, 'card', getMediaUrl(classified.gallery?.[0], 'card', FALLBACK_RESULT_IMAGE)),
        isFavorited: favoriteStates[classified.id] ?? classified.is_favorited ?? false,
        isHighlighted: classified.is_highlighted,
        isCotaNautica: classified.is_cota_nautica,
    }));
};

const toListingCards = (listings: Listing[], favoriteStates: Record<number, boolean>): ListingCardProps[] => {
    return listings.map((listing) => {
        const category = listing.categories?.[0];
        const averageRating = (listing as Listing & { average_rating?: number }).average_rating ?? 0;
        const reviewCount = (listing as Listing & { review_count?: number }).review_count ?? 0;

        return {
            id: listing.id,
            title: listing.title,
            slug: listing.slug,
            category: category?.name || '',
            categorySlug: category?.slug,
            location: formatLocation(listing.city?.name, listing.state?.name),
            averageRating,
            reviewCount,
            summary: listing.summary,
            imageUrl: getMediaUrl(listing.logo, 'logo', getMediaUrl(listing.gallery?.[0], 'card')),
            isFavorited: favoriteStates[listing.id] ?? listing.is_favorited ?? false,
            isVerified: false,
            isFeatured: Boolean(listing.level?.priority && listing.level.priority > 0),
        };
    });
};

export default function Results({
    results,
    filterOptions,
    filters: initialFilters,
    type = 'embarcacoes',
    classifiedBanners = [],
    categorySeo = null,
}: Props) {
    const { auth, appUrl } = usePage<SharedData>().props;
    const pageType: ResultsType = type === 'empresas' ? 'empresas' : 'embarcacoes';

    const [favoriteStates, setFavoriteStates] = useState<Record<number, boolean>>({});
    const [filters, setFilters] = useState<BoatFilters>(() => buildInitialBoatFilters(initialFilters, filterOptions));
    const [listingFilters, setListingFilters] = useState<ListingFilters>(() => buildInitialListingFilters(initialFilters));
    const [sortBy, setSortBy] = useState<SortOption>((initialFilters.sort as SortOption) || 'relevance');
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const [searchName, setSearchName] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [isSeoLongContentOpen, setIsSeoLongContentOpen] = useState(false);
    const [openFaqIndexes, setOpenFaqIndexes] = useState<Set<number>>(new Set());

    const boatSidebarClass = 'lg:h-full';
    const listingSidebarClass = 'lg:h-full lg:overflow-y-auto lg:overscroll-contain';

    const timeoutRef = useRef<ReturnType<typeof window.setTimeout> | undefined>(undefined);
    const isInitialMount = useRef(true);
    const lastSearchSignatureRef = useRef('');
    const handledAutoFavoriteRef = useRef<string | null>(null);
    const previousPageTypeRef = useRef<ResultsType>(pageType);
    const prevInitialCategoryKeyRef = useRef<string>(JSON.stringify(normalizeCategory(initialFilters.category).sort()));

    const yearMin = filterOptions?.yearRange?.min ?? DEFAULT_YEAR_MIN;
    const yearMax = filterOptions?.yearRange?.max ?? new Date().getFullYear();
    const priceMin = filterOptions?.priceRange?.min ?? 0;
    const priceMax = filterOptions?.priceRange?.max ?? DEFAULT_PRICE_MAX;

    const categoryBreadcrumbs = useMemo(() => normalizeBreadcrumbs(categorySeo?.breadcrumbs), [categorySeo?.breadcrumbs]);
    const isCategorySearchPage = pageType === 'embarcacoes' && Boolean(categorySeo?.friendly_url_path);
    const categoryHeadingTitle = normalizeText(categorySeo?.seo_title) || normalizeText(categorySeo?.seo_h1) || normalizeText(categorySeo?.name);
    const categorySchemaName = normalizeText(categorySeo?.seo_h1) || categoryHeadingTitle;
    const categoryIntroText = normalizeText(categorySeo?.intro_text);
    const categoryMetaDescription = truncateText(normalizeText(categorySeo?.seo_description) || stripHtml(categoryIntroText), 160);
    const canonicalUrl = isCategorySearchPage ? toAbsoluteUrl(appUrl, categorySeo?.friendly_url_path ?? '/') : '';
    const pageTitle = isCategorySearchPage
        ? normalizeText(categorySeo?.seo_title)
            ? `${normalizeText(categorySeo?.seo_title)} | Compre Náutica`
            : `${categoryHeadingTitle || 'Categoria'} à venda | Compre Náutica`
        : 'Buscar - Compre Nautica';
    const postListingTitle = normalizeText(categorySeo?.seo_h2) || `Mais sobre ${categoryHeadingTitle || 'esta categoria'}`;
    const resumoRapido = normalizeText(categorySeo?.resumo_rapido);
    const longDescription = normalizeText(categorySeo?.long_description);
    const pontosDestaque = useMemo(() => normalizeStringArray(categorySeo?.pontos_destaque), [categorySeo?.pontos_destaque]);
    const relatedTags = useMemo(() => normalizeStringArray(categorySeo?.related_tags), [categorySeo?.related_tags]);
    const synonyms = useMemo(() => normalizeStringArray(categorySeo?.synonyms), [categorySeo?.synonyms]);
    const faqItems = useMemo(() => normalizeFaqItems(categorySeo?.faq_json), [categorySeo?.faq_json]);
    const categorySocialImage = getMediaUrl(categorySeo?.logo, 'social', getMediaUrl(categorySeo?.icone, 'social'));

    const categoryPathById = useMemo(() => {
        const lookup = new Map<number, string>();
        const catById = new Map(filterOptions.categories.map((c) => [c.id, c]));

        for (const category of filterOptions.categories) {
            const slugs: string[] = [];
            let current: FilterOption | undefined = category;
            while (current) {
                if (current.slug) {
                    slugs.unshift(current.slug);
                }
                current = current.parent_id != null ? catById.get(current.parent_id) : undefined;
            }
            if (slugs.length > 0 && slugs.every(Boolean)) {
                lookup.set(category.id, '/' + slugs.join('/'));
            }
        }

        return lookup;
    }, [filterOptions.categories]);

    const routeCategoryIds = useMemo(() => new Set(normalizeCategory(initialFilters.category).map(Number)), [initialFilters.category]);

    const resultsRouteUrl = useMemo(() => {
        if (!isCategorySearchPage) {
            return search().url;
        }

        // Strip the implicit route category so we count only user-added extras
        const userSelected = filters.category.filter((id) => !routeCategoryIds.has(Number(id)));

        if (userSelected.length === 0) {
            return categorySeo?.friendly_url_path ?? search().url;
        }

        if (userSelected.length === 1) {
            const path = categoryPathById.get(Number(userSelected[0]));
            if (path) {
                return path;
            }
            return categorySeo?.friendly_url_path ?? search().url;
        }

        return search().url;
    }, [isCategorySearchPage, filters.category, routeCategoryIds, categoryPathById, categorySeo?.friendly_url_path]);

    useEffect(() => {
        if (previousPageTypeRef.current === pageType) {
            return;
        }

        previousPageTypeRef.current = pageType;
        setFilters(buildInitialBoatFilters(initialFilters, filterOptions));
        setListingFilters(buildInitialListingFilters(initialFilters));
        setSortBy((initialFilters.sort as SortOption) || 'relevance');
        isInitialMount.current = true;
        lastSearchSignatureRef.current = '';
    }, [filterOptions, initialFilters, pageType]);

    // When navigating between CategoryRoute pages (e.g. /lanchas → /lanchas/nx-boats),
    // Inertia preserves filter state but initialFilters.category reflects the new page.
    // Reset filters.category to match so stale parent IDs don't trigger a reverse navigation.
    useEffect(() => {
        if (!isCategorySearchPage) return;
        const key = JSON.stringify(normalizeCategory(initialFilters.category).sort());
        if (prevInitialCategoryKeyRef.current === key) return;
        prevInitialCategoryKeyRef.current = key;
        setFilters((prev) => ({ ...prev, category: normalizeCategory(initialFilters.category) }));
        isInitialMount.current = true;
        lastSearchSignatureRef.current = '';
    }, [initialFilters.category, isCategorySearchPage]);

    const collectionPageSchema = useMemo(() => {
        if (!isCategorySearchPage || !canonicalUrl) {
            return null;
        }

        return {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl}#collection`,
            name: categorySchemaName || categoryHeadingTitle,
            url: canonicalUrl,
            description: categoryMetaDescription,
            inLanguage: 'pt-BR',
        };
    }, [canonicalUrl, categoryHeadingTitle, categoryMetaDescription, categorySchemaName, isCategorySearchPage]);

    const breadcrumbSchema = useMemo(() => {
        if (!isCategorySearchPage || !canonicalUrl) {
            return null;
        }

        const items = [
            {
                '@type': 'ListItem',
                position: 1,
                name: 'Início',
                item: toAbsoluteUrl(appUrl, '/'),
            },
            ...categoryBreadcrumbs.map((breadcrumb, index) => ({
                '@type': 'ListItem',
                position: index + 2,
                name: breadcrumb.name,
                item: toAbsoluteUrl(appUrl, breadcrumb.path),
            })),
        ];

        return {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: items,
        };
    }, [appUrl, canonicalUrl, categoryBreadcrumbs, isCategorySearchPage]);

    const itemListSchema = useMemo(() => {
        const categoryResults = ((results?.data || []) as Classified[]).filter((item) => typeof item?.slug === 'string' && item.slug.length > 0);

        if (!isCategorySearchPage || categoryResults.length === 0) {
            return null;
        }

        const startPosition = results?.from ?? 1;

        return {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListOrder: 'https://schema.org/ItemListOrderDescending',
            numberOfItems: results?.total ?? categoryResults.length,
            itemListElement: categoryResults.map((classified, index) => ({
                '@type': 'ListItem',
                position: startPosition + index,
                url: toAbsoluteUrl(appUrl, buildClassifiedPath(classified.slug)),
            })),
        };
    }, [appUrl, isCategorySearchPage, results?.data, results?.from, results?.total]);

    const faqSchema = useMemo(() => {
        if (!isCategorySearchPage || faqItems.length === 0) {
            return null;
        }

        return {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqItems.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer,
                },
            })),
        };
    }, [faqItems, isCategorySearchPage]);

    const sanitizedIntroHtml = useMemo(() => sanitizeHtmlContent(categoryIntroText), [categoryIntroText]);
    const sanitizedLongDescriptionHtml = useMemo(() => sanitizeHtmlContent(longDescription), [longDescription]);
    const hasPostListingContent =
        Boolean(normalizeText(categorySeo?.seo_h2)) ||
        Boolean(resumoRapido) ||
        Boolean(longDescription) ||
        pontosDestaque.length > 0 ||
        faqItems.length > 0 ||
        relatedTags.length > 0 ||
        synonyms.length > 0;

    const categorySlugById = useMemo(() => {
        const lookup = new Map<number, string>();

        for (const category of filterOptions.categories) {
            if (category.slug) {
                lookup.set(category.id, category.slug);
            }
        }

        return lookup;
    }, [filterOptions.categories]);

    const listings = useMemo(() => {
        if (pageType !== 'empresas') {
            return [];
        }

        return (results?.data || []) as Listing[];
    }, [pageType, results?.data]);

    const classifieds = useMemo(() => {
        if (pageType !== 'embarcacoes') {
            return [];
        }

        return (results?.data || []) as Classified[];
    }, [pageType, results?.data]);

    const listingsById = useMemo(() => {
        return new Map(listings.map((listing) => [listing.id, listing]));
    }, [listings]);

    const classifiedsById = useMemo(() => {
        return new Map(classifieds.map((classified) => [classified.id, classified]));
    }, [classifieds]);

    const boatCards = useMemo(() => toBoatCards(classifieds, favoriteStates), [classifieds, favoriteStates]);
    const listingCards = useMemo(() => toListingCards(listings, favoriteStates), [listings, favoriteStates]);

    const buildSearchParams = useCallback(
        (searchTerm = initialFilters.search): ResultsSearchParams => {
            const searchParams: ResultsSearchParams = {
                sort: sortBy,
                type: pageType,
            };

            if (searchTerm?.trim()) {
                searchParams.search = searchTerm.trim();
            }

            const activeFilters = pageType === 'empresas' ? listingFilters : filters;

            // Strip implicit route category to find user-selected extras
            const userSelectedCategories = isCategorySearchPage
                ? activeFilters.category.filter((id) => !routeCategoryIds.has(Number(id)))
                : activeFilters.category;

            if (userSelectedCategories.length > 0) {
                const singleCategoryPath =
                    isCategorySearchPage && userSelectedCategories.length === 1 && categoryPathById.has(Number(userSelectedCategories[0]));

                if (!singleCategoryPath) {
                    const categorySlugs = userSelectedCategories
                        .map((categoryId) => categorySlugById.get(Number(categoryId)))
                        .filter((slug): slug is string => Boolean(slug));

                    if (categorySlugs.length === 1) {
                        searchParams.category = categorySlugs[0];
                    } else if (categorySlugs.length > 1) {
                        searchParams.category = categorySlugs;
                    }
                }
                // singleCategoryPath = true → category encoded in URL path, no query param needed
            }
            // 0 user extras on CategoryRoute → URL path handles it, no query param needed

            if (activeFilters.location.length > 0) {
                searchParams.state = activeFilters.location;
            }

            if (pageType === 'embarcacoes') {
                if (filters.condition !== 'all') {
                    searchParams.condition = filters.condition;
                }

                if (filters.yearRange[0] !== yearMin) {
                    searchParams.year_min = filters.yearRange[0];
                }
                if (filters.yearRange[1] !== yearMax) {
                    searchParams.year_max = filters.yearRange[1];
                }

                if (filters.priceRange[0] !== priceMin) {
                    searchParams.price_min = filters.priceRange[0];
                }
                if (filters.priceRange[1] !== priceMax) {
                    searchParams.price_max = filters.priceRange[1];
                }

                if (filters.hours[0] > 0) {
                    searchParams.sailing_time_min = filters.hours[0];
                }
                if (filters.hours[1] < DEFAULT_SAILING_MAX) {
                    searchParams.sailing_time_max = filters.hours[1];
                }
            }

            return searchParams;
        },
        [
            categorySeo?.slug,
            categoryPathById,
            categorySlugById,
            filters,
            initialFilters.search,
            isCategorySearchPage,
            listingFilters,
            pageType,
            priceMax,
            priceMin,
            routeCategoryIds,
            sortBy,
            yearMax,
            yearMin,
        ],
    );

    useEffect(() => {
        const searchParams = buildSearchParams();
        const nextSignature = JSON.stringify({ url: resultsRouteUrl, params: searchParams });

        if (isInitialMount.current) {
            isInitialMount.current = false;
            lastSearchSignatureRef.current = nextSignature;
            return;
        }

        if (nextSignature === lastSearchSignatureRef.current) {
            return;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = window.setTimeout(() => {
            lastSearchSignatureRef.current = nextSignature;

            router.get(resultsRouteUrl, searchParams as InertiaPayload, {
                preserveState: true,
                preserveScroll: true,
                replace: true,
            });
        }, 300);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [buildSearchParams, resultsRouteUrl]);

    useEffect(() => {
        setIsSeoLongContentOpen(false);
        setOpenFaqIndexes(new Set());
    }, [categorySeo?.id]);

    const favoriteParamKey = pageType === 'empresas' ? 'favorite_listing_id' : 'favorite_id';

    const clearAutoFavoriteParams = useCallback(
        (url: URL) => {
            url.searchParams.delete('auto_favorite');
            url.searchParams.delete(favoriteParamKey);
            window.history.replaceState(null, '', url.toString());
        },
        [favoriteParamKey],
    );

    const favoriteEndpoint = useCallback(
        (resultId: number) => {
            return pageType === 'empresas' ? `/api/favorites/listing/${resultId}/toggle` : `/api/favorites/${resultId}/toggle`;
        },
        [pageType],
    );

    const redirectToLoginForFavorite = useCallback(
        (resultId: number) => {
            if (typeof window === 'undefined') {
                return;
            }

            const currentUrl = new URL(window.location.href);
            currentUrl.searchParams.set('auto_favorite', '1');
            currentUrl.searchParams.set(favoriteParamKey, String(resultId));

            router.visit(login().url, {
                data: { return_url: currentUrl.toString() },
            });
        },
        [favoriteParamKey],
    );

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        if (!auth?.user) {
            return;
        }

        try {
            const url = new URL(window.location.href);
            const autoFavorite = url.searchParams.get('auto_favorite');
            const favoriteIdParam = url.searchParams.get(favoriteParamKey);

            if (autoFavorite !== '1' || !favoriteIdParam) {
                return;
            }

            const signature = `${favoriteParamKey}:${favoriteIdParam}`;
            if (handledAutoFavoriteRef.current === signature) {
                return;
            }

            const favoriteId = Number(favoriteIdParam);
            if (!favoriteId || Number.isNaN(favoriteId)) {
                clearAutoFavoriteParams(url);
                return;
            }

            const resultItems = (results?.data || []) as Array<{ id: number; is_favorited?: boolean }>;
            const targetItem = resultItems.find((item) => item.id === favoriteId);

            if (!targetItem) {
                clearAutoFavoriteParams(url);
                return;
            }

            const alreadyFavorited = favoriteStates[favoriteId] ?? targetItem.is_favorited ?? false;
            if (alreadyFavorited) {
                handledAutoFavoriteRef.current = signature;
                clearAutoFavoriteParams(url);
                return;
            }

            handledAutoFavoriteRef.current = signature;

            apiClient
                .post<{ is_favorited: boolean }>(favoriteEndpoint(favoriteId))
                .then((response) => {
                    setFavoriteStates((previous) => ({
                        ...previous,
                        [favoriteId]: response.data.is_favorited,
                    }));
                })
                .catch((error) => {
                    console.error('Error auto-toggling favorite after login on results page:', error);
                })
                .finally(() => {
                    clearAutoFavoriteParams(url);
                });
        } catch (error) {
            console.error('Error handling auto_favorite params on results page:', error);
        }
    }, [auth?.user, clearAutoFavoriteParams, favoriteEndpoint, favoriteParamKey, favoriteStates, results?.data]);

    const handleFiltersChange = (newFilters: BoatFilters) => {
        setFilters(newFilters);
    };

    const handleListingFiltersChange = (newFilters: ListingFilters) => {
        setListingFilters(newFilters);
    };

    const handleSortChange = (newSort: SortOption) => {
        setSortBy(newSort);
    };

    const handleFaqToggle = (faqIndex: number, isOpen: boolean) => {
        setOpenFaqIndexes((previous) => {
            const updated = new Set(previous);

            if (isOpen) {
                updated.add(faqIndex);
            } else {
                updated.delete(faqIndex);
            }

            return updated;
        });
    };

    const handleSearchSubmit = useCallback(
        (query: string) => {
            const searchParams = buildSearchParams(query);
            lastSearchSignatureRef.current = JSON.stringify(searchParams);

            router.get(resultsRouteUrl, searchParams as InertiaPayload, {
                preserveState: false,
                preserveScroll: false,
            });
        },
        [buildSearchParams, resultsRouteUrl],
    );

    const handleSaveSearch = () => {
        setShowSaveDialog(true);
        setSearchName('');
        setSaveSuccess(false);
    };

    const handleConfirmSave = () => {
        const trimmedName = searchName.trim();
        if (!trimmedName) {
            return;
        }

        const savedSearch = {
            id: Date.now().toString(),
            name: trimmedName,
            filters: pageType === 'empresas' ? listingFilters : filters,
            sortBy,
            type: pageType,
            createdAt: new Date().toISOString(),
        };

        try {
            const existingSaves = JSON.parse(localStorage.getItem('savedBoatSearches') || '[]');
            const updatedSaves = [...existingSaves, savedSearch];
            localStorage.setItem('savedBoatSearches', JSON.stringify(updatedSaves));

            setSaveSuccess(true);
            window.setTimeout(() => {
                setShowSaveDialog(false);
                setSaveSuccess(false);
            }, 2000);
        } catch (error) {
            console.error('Error saving search on results page:', error);
        }
    };

    const handleFavorite = async (resultId: number) => {
        if (!auth?.user) {
            redirectToLoginForFavorite(resultId);
            return;
        }

        try {
            const response = await apiClient.post<{ is_favorited: boolean }>(favoriteEndpoint(resultId));

            setFavoriteStates((previous) => ({
                ...previous,
                [resultId]: response.data.is_favorited,
            }));
        } catch (error: unknown) {
            if (hasHttpStatus(error, 401)) {
                redirectToLoginForFavorite(resultId);
                return;
            }

            console.error('Error toggling favorite:', error);
        }
    };

    const handleViewOffer = (resultId: number) => {
        if (pageType === 'empresas') {
            const listing = listingsById.get(resultId);
            if (listing) {
                router.visit(showListing(listing.slug).url);
            }
            return;
        }

        const classified = classifiedsById.get(resultId);
        if (classified) {
            router.visit(buildClassifiedPath(classified.slug));
        }
    };

    return (
        <FrontendLayout>
            <Head title={pageTitle}>
                {isCategorySearchPage && categoryMetaDescription && <meta name="description" content={categoryMetaDescription} />}
                {isCategorySearchPage && canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
                {isCategorySearchPage && <meta name="robots" content="index,follow" />}

                {isCategorySearchPage && <meta property="og:title" content={pageTitle} />}
                {isCategorySearchPage && categoryMetaDescription && <meta property="og:description" content={categoryMetaDescription} />}
                {isCategorySearchPage && canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
                {isCategorySearchPage && <meta property="og:type" content="website" />}
                {isCategorySearchPage && <meta property="og:locale" content="pt_BR" />}
                {isCategorySearchPage && categorySocialImage && <meta property="og:image" content={categorySocialImage} />}

                {collectionPageSchema && (
                    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(collectionPageSchema) }} />
                )}
                {breadcrumbSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />}
                {itemListSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />}
                {faqSchema && <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />}

                <link rel="preconnect" href="https://fonts.bunny.net" />
                <link href="https://fonts.bunny.net/css?family=instrument-sans:400,500,600" rel="stylesheet" />
            </Head>

            <div className="w-full bg-gray-100 pb-24">
                <div className="container mx-auto px-4 py-8">
                    {isCategorySearchPage && (
                        <section className="mb-6 rounded-xl bg-white p-4 sm:p-6">
                            <nav aria-label="Breadcrumb" className="mb-2">
                                <ol className="flex flex-wrap items-center gap-2 text-xs text-gray-500 sm:text-sm">
                                    <li>
                                        <a href="/" className="transition-colors hover:text-gray-700">
                                            Início
                                        </a>
                                    </li>

                                    {categoryBreadcrumbs.map((breadcrumb, index) => {
                                        const isLast = index === categoryBreadcrumbs.length - 1;

                                        return (
                                            <li key={`${breadcrumb.path}-${index}`} className="inline-flex items-center gap-2">
                                                <span aria-hidden="true">/</span>
                                                {isLast ? (
                                                    <span className="font-medium text-gray-700">{breadcrumb.name}</span>
                                                ) : (
                                                    <a href={breadcrumb.path} className="transition-colors hover:text-gray-700">
                                                        {breadcrumb.name}
                                                    </a>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ol>
                            </nav>

                            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl lg:text-3xl">
                                {categoryHeadingTitle || 'Embarcações à venda'}
                            </h1>

                            {categoryIntroText && (
                                <div
                                    className="prose prose-neutral mt-3 max-w-none text-sm text-gray-700 sm:text-base"
                                    dangerouslySetInnerHTML={{ __html: sanitizedIntroHtml }}
                                />
                            )}
                        </section>
                    )}

                    <div className="mb-6">
                        <SearchAutocomplete
                            placeholder={pageType === "empresas" ? "Busque por serviço, empresa ou local" : "Busque por marca, modelo ou local"}
                            initialValue={initialFilters.search || ''}
                            showButton={false}
                            searchType={pageType}
                            onSearch={handleSearchSubmit}
                        />
                    </div>

                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[300px_1fr]">
                        <div className="lg:sticky lg:top-8 lg:h-[calc(100dvh-2rem)] lg:self-start">
                            {pageType === 'embarcacoes' ? (
                                <BoatFilterSidebar
                                    filters={filters}
                                    onFiltersChange={handleFiltersChange}
                                    onSaveSearch={handleSaveSearch}
                                    filterOptions={filterOptions}
                                    className={boatSidebarClass}
                                />
                            ) : (
                                <ListingFilterSidebar
                                    filters={listingFilters}
                                    onFiltersChange={handleListingFiltersChange}
                                    onSaveSearch={handleSaveSearch}
                                    filterOptions={filterOptions}
                                    className={listingSidebarClass}
                                />
                            )}
                        </div>

                        <div className="space-y-6">
                            <BoatResultsHeader
                                totalResults={results?.total || 0}
                                sortBy={sortBy}
                                onSortChange={handleSortChange}
                                resultLabel={pageType === 'empresas' ? 'empresas encontradas' : 'anúncios encontrados'}
                            />

                            {pageType === 'embarcacoes' ? (
                                <BoatResultsGrid
                                    boats={boatCards}
                                    onFavorite={handleFavorite}
                                    onViewOffer={handleViewOffer}
                                    type="embarcacoes"
                                    classifiedBanners={classifiedBanners}
                                />
                            ) : (
                                <ListingResultsGrid listings={listingCards} onFavorite={handleFavorite} onViewProfile={handleViewOffer} />
                            )}

                            {results && <Pagination data={results} preserveState={false} className="mt-8" />}
                        </div>
                    </div>

                    {isCategorySearchPage && hasPostListingContent && (
                        <section className="mt-8 space-y-5 rounded-xl bg-white p-4 sm:p-6">
                            <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">{postListingTitle}</h2>

                            {resumoRapido && <p className="text-sm leading-relaxed text-gray-700 sm:text-base">{resumoRapido}</p>}

                            {pontosDestaque.length > 0 && (
                                <ul className="list-disc space-y-2 pl-5 text-sm text-gray-700 sm:text-base">
                                    {pontosDestaque.map((item, index) => (
                                        <li key={`${item}-${index}`}>{item}</li>
                                    ))}
                                </ul>
                            )}

                            {longDescription && (
                                <div className="rounded-lg border border-gray-200 p-4">
                                    <Collapsible open={isSeoLongContentOpen} onOpenChange={setIsSeoLongContentOpen}>
                                        <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 text-left">
                                            <span className="text-sm font-medium text-gray-900 sm:text-base">
                                                {isSeoLongContentOpen
                                                    ? `Ocultar conteúdo sobre ${categorySchemaName || categoryHeadingTitle || 'a categoria'}`
                                                    : `Ler mais sobre ${categorySchemaName || categoryHeadingTitle || 'a categoria'}`}
                                            </span>
                                            {isSeoLongContentOpen ? (
                                                <ChevronUp className="h-4 w-4 text-gray-500" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4 text-gray-500" />
                                            )}
                                        </CollapsibleTrigger>

                                        <CollapsibleContent forceMount className={isSeoLongContentOpen ? 'pt-4' : 'hidden pt-4'}>
                                            <div
                                                className="prose prose-neutral max-w-none text-sm text-gray-700 sm:text-base"
                                                dangerouslySetInnerHTML={{ __html: sanitizedLongDescriptionHtml }}
                                            />
                                        </CollapsibleContent>
                                    </Collapsible>
                                </div>
                            )}

                            {faqItems.length > 0 && (
                                <div className="space-y-3">
                                    <h3 className="text-base font-semibold text-gray-900 sm:text-lg">Perguntas frequentes</h3>

                                    {faqItems.map((faq, index) => {
                                        const isOpen = openFaqIndexes.has(index);

                                        return (
                                            <Collapsible
                                                key={`${faq.question}-${index}`}
                                                open={isOpen}
                                                onOpenChange={(open: boolean) => handleFaqToggle(index, open)}
                                            >
                                                <div className="rounded-lg border border-gray-200">
                                                    <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 p-4 text-left">
                                                        <span className="text-sm font-medium text-gray-900 sm:text-base">{faq.question}</span>
                                                        {isOpen ? (
                                                            <ChevronUp className="h-4 w-4 text-gray-500" />
                                                        ) : (
                                                            <ChevronDown className="h-4 w-4 text-gray-500" />
                                                        )}
                                                    </CollapsibleTrigger>

                                                    <CollapsibleContent forceMount className={isOpen ? 'px-4 pt-0 pb-4' : 'hidden px-4 pt-0 pb-4'}>
                                                        <p className="text-sm leading-relaxed text-gray-700 sm:text-base">{faq.answer}</p>
                                                    </CollapsibleContent>
                                                </div>
                                            </Collapsible>
                                        );
                                    })}
                                </div>
                            )}

                            {(relatedTags.length > 0 || synonyms.length > 0) && (
                                <div className="space-y-4">
                                    {relatedTags.length > 0 && (
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">Tags relacionadas</h3>
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {relatedTags.map((tag, index) => (
                                                    <a
                                                        key={`${tag}-${index}`}
                                                        href={`${search().url}?type=embarcacoes&search=${encodeURIComponent(tag)}`}
                                                        className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-100"
                                                    >
                                                        {tag}
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {synonyms.length > 0 && (
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 sm:text-base">Termos similares</h3>
                                            <div className="mt-2 flex flex-wrap gap-2">
                                                {synonyms.map((term, index) => (
                                                    <a
                                                        key={`${term}-${index}`}
                                                        href={`${search().url}?type=embarcacoes&search=${encodeURIComponent(term)}`}
                                                        className="rounded-full border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-100"
                                                    >
                                                        {term}
                                                    </a>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </section>
                    )}

                    <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
                        <DialogContent className="sm:max-w-md">
                            <DialogHeader>
                                <DialogTitle>Salvar pesquisa</DialogTitle>
                                <DialogDescription>Dê um nome para esta pesquisa para acessá-la facilmente no futuro.</DialogDescription>
                            </DialogHeader>

                            {!saveSuccess ? (
                                <>
                                    <div className="space-y-2">
                                        <Label htmlFor="search-name">Nome da pesquisa</Label>
                                        <Input
                                            id="search-name"
                                            value={searchName}
                                            onChange={(event) => setSearchName(event.target.value)}
                                            placeholder="Ex: Lanchas no Rio de Janeiro"
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' && searchName.trim()) {
                                                    handleConfirmSave();
                                                }
                                            }}
                                        />
                                    </div>

                                    <DialogFooter>
                                        <Button variant="outline" onClick={() => setShowSaveDialog(false)}>
                                            Cancelar
                                        </Button>
                                        <Button onClick={handleConfirmSave} disabled={!searchName.trim()} className="bg-green-600 hover:bg-green-700">
                                            Salvar
                                        </Button>
                                    </DialogFooter>
                                </>
                            ) : (
                                <div className="flex flex-col items-center py-6 text-center">
                                    <div className="mb-3 text-4xl">✅</div>
                                    <h3 className="mb-2 text-lg font-semibold text-green-700">Pesquisa salva!</h3>
                                    <p className="text-sm text-gray-600">Sua pesquisa "{searchName}" foi salva com sucesso.</p>
                                </div>
                            )}
                        </DialogContent>
                    </Dialog>
                </div>
            </div>
        </FrontendLayout>
    );
}
